/**
 * 自動通知 > 経理担当 の「i」アイコンで開く送信内容の例。
 *
 * 文面は実際の送信と同じ組み立て関数 (shared/types/accounting-notify.ts) に
 * 例のデータを入れて作る。表示しているのは Chatwork 用の文面。
 */
import type { AccountingNotifyTrigger, AccountingNoticeText } from '@goenhub/shared';
import {
  buildCommissionInvoiceReceivedNotice,
  buildCommissionPaymentRequestNotice,
  buildSaRenewalDueNotice,
  buildTransferAddedNotice,
  buildTransferBatchNotice,
  transferRequestsAdminUrl,
} from '@goenhub/shared';

const SAMPLE_ITEMS = [
  { customerName: '山田 太郎', invoiceNumber: 'TWHA-000001', finalAmount: 579.15, currency: 'EUR' },
  { customerName: '佐藤 花子', invoiceNumber: 'TWHA-000002', finalAmount: 286.65, currency: 'EUR' },
  { customerName: '鈴木 一郎', invoiceNumber: 'TWHA-000003', finalAmount: 932.75, currency: 'EUR' },
];

function sampleText(trigger: AccountingNotifyTrigger): AccountingNoticeText {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  switch (trigger) {
    case 'transfer_request_added':
      return buildTransferAddedNotice({
        categoryName: 'TimeWaver',
        item: SAMPLE_ITEMS[0],
        url: transferRequestsAdminUrl(origin),
      });
    case 'transfer_request_batch':
      return buildTransferBatchNotice({
        categoryName: 'TimeWaver',
        items: SAMPLE_ITEMS,
        url: transferRequestsAdminUrl(origin),
      });
    case 'commission_payment_request':
      return buildCommissionPaymentRequestNotice({
        recipientName: '田中 次郎',
        recipientTypeLabel: 'SA',
        amountLabel: '¥30,000',
        dealTitle: 'TimeWaver Pro 購入',
        contactName: '山田 太郎',
        bankInfo: '〇〇銀行 △△支店 / 普通 1234567 / タナカ ジロウ',
        url: `${origin}/finance?hub=ledger`,
      });
    case 'commission_invoice_received':
      return buildCommissionInvoiceReceivedNotice({
        saName: '田中 次郎',
        invoiceNumber: 'SA-2026-10-001',
        periodLabel: '2026年10月分',
        lineCount: 3,
        totalLabel: '¥99,000',
        url: `${origin}/c/sa-invoice/xxxxxxxx`,
      });
    case 'sa_renewal_due_internal':
      return buildSaRenewalDueNotice({
        saName: '田中 次郎',
        saEmail: 'tanaka@example.com',
        reasonLabel: '契約終了が接近',
        contractEndDate: '2026-11-30',
        daysLeft: 30,
        url: `${origin}/sales/sa-management`,
      });
  }
}

export function AccountingNoticeExample({
  trigger,
  mailSource,
}: {
  trigger: AccountingNotifyTrigger;
  mailSource: string;
}) {
  const text = sampleText(trigger);
  return (
    <div className="mt-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-2">
      <p className="text-[11px] font-semibold text-[var(--text-secondary)]">
        送信内容の例（Chatwork）
      </p>
      <pre className="whitespace-pre-wrap break-words text-[11px] leading-relaxed font-mono text-[var(--text-primary)] bg-[var(--bg-surface)] border border-[var(--border)] rounded p-2">
        {text.chatwork}
      </pre>
      <ul className="text-[11px] text-[var(--text-muted)] space-y-0.5 list-disc pl-4">
        <li>Google Chat: 同じ内容（タイトルは太字、[hr] は罫線で表示）</li>
        <li>メール: {mailSource}</li>
        <li>金額・氏名などは例です。</li>
      </ul>
    </div>
  );
}
